import React from 'react';
import { Form, InputNumber, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * WP-08 — the "current mileage" prompt shown on estimates and work orders.
 *
 * Deliberately NOT a required field. The owner's instruction was that the
 * document should ASK for mileage, and a hard requirement would block a job in
 * the field when the cluster is dead, the dash is inaccessible, or the tech is
 * simply mid-repair. Missing mileage is surfaced as a hint instead, because
 * WP-13's service intervals cannot be computed without it.
 */

export interface OdometerFieldProps {
  /** antd Form.Item name. Defaults to the column name on both documents. */
  name?: string;
  label?: string;
  /** The vehicle's last known reading, used for the "last recorded" hint. */
  lastKnownMileage?: number | null;
  disabled?: boolean;
  /** Show the "no mileage on file" warning when nothing has been entered. */
  flagWhenMissing?: boolean;
  /** Current value, only used to decide whether to show the missing-mileage hint. */
  value?: number | null;
}

export const ODOMETER_MAX = 9_999_999;

const OdometerField: React.FC<OdometerFieldProps> = ({
  name = 'intake_odometer',
  label = 'Current Mileage',
  lastKnownMileage,
  disabled,
  flagWhenMissing = true,
  value,
}) => {
  const isMissing = value == null;

  return (
    <Form.Item
      name={name}
      label={
        <span>
          {label}{' '}
          <Tooltip title="Odometer reading taken at intake. Recorded against the vehicle's service history so future service intervals can be calculated.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
      rules={[
        { type: 'number', min: 0, message: 'Mileage cannot be negative' },
        { type: 'number', max: ODOMETER_MAX, message: 'Mileage seems unreasonably high' },
      ]}
      extra={
        <>
          {lastKnownMileage != null && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last recorded: {lastKnownMileage.toLocaleString()} mi
            </Text>
          )}
          {flagWhenMissing && isMissing && lastKnownMileage == null && (
            <Text type="warning" style={{ fontSize: 12 }}>
              No mileage on file — service intervals cannot be calculated without it.
            </Text>
          )}
        </>
      }
    >
      <InputNumber
        placeholder="84,210"
        style={{ width: '100%' }}
        min={0}
        max={ODOMETER_MAX}
        disabled={disabled}
        // Digits only. The backend rejects fractional and non-numeric readings
        // outright, so the control should not let one be typed in the first place.
        precision={0}
        formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={(v) => (v ? Number(v.replace(/[^0-9]/g, '')) : null) as any}
        inputMode="numeric"
      />
    </Form.Item>
  );
};

export default OdometerField;
